import { useEffect, useState } from 'react'
import { Check, Users } from 'lucide-react'
import { useSocialStore, displayName } from '@/store/social'
import { socialApi } from '@/lib/socialApi'
import { describeError } from '@/lib/auth'
import { avatarUrl } from '@/lib/avatar'
import { plural } from '@/lib/guildUi'
import { cx } from '@/lib/cx'
import type { Message, Poll, User } from '@/types/api'
import { Modal } from '../settings/Modal'
import { Avatar } from '../Avatar'
import { EmojiGlyph } from './EmojiButton'
import styles from './PollCard.module.css'

/** Сколько осталось до конца опроса: «5 мин», «23 ч», до двух суток — в часах, дальше «3 дн.». */
function timeLeft(expiry: string | null, now: number): string | null {
  if (!expiry) return null
  const ms = new Date(expiry).getTime() - now
  if (ms <= 0) return null
  const min = Math.ceil(ms / 60000)
  if (min < 60) return `${min} мин`
  const hours = Math.round(min / 60)
  if (hours < 48) return `${hours} ч`
  return `${Math.floor(hours / 24)} дн.`
}

function votesLabel(n: number): string {
  return `${n} ${plural(n, 'голос', 'голоса', 'голосов')}`
}

/**
 * Опрос в сообщении. Пока не голосовал — варианты выбираются и отправляются кнопкой «Проголосовать»;
 * после голоса и когда опрос завершён — итоги полосками. Свой голос снимается, пока опрос идёт;
 * автор может подвести итоги раньше срока. Число голосов открывает, кто за что голосовал.
 */
export function PollCard({ message, channelId, mine }: { message: Message; channelId: string; mine: boolean }) {
  const poll = message.poll as Poll
  const votePoll = useSocialStore((s) => s.votePoll)
  const endPoll = useSocialStore((s) => s.endPoll)
  const [selected, setSelected] = useState<number[]>([])
  const [peek, setPeek] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [voters, setVoters] = useState<number | null>(null)
  const [now, setNow] = useState(() => Date.now())

  // обратный отсчёт идёт сам: раз в минуту, пока опрос не завершён
  const finalized = Boolean(poll.results?.is_finalized)
  useEffect(() => {
    if (finalized) return
    const t = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(t)
  }, [finalized])

  const counts = poll.results?.answer_counts ?? []
  const countOf = (id: number) => counts.find((c) => c.id === id)?.count ?? 0
  const votedFor = counts.filter((c) => c.me_voted).map((c) => c.id)
  const total = counts.reduce((sum, c) => sum + c.count, 0)
  const left = timeLeft(poll.expiry, now)
  const closed = finalized || !left
  const results = closed || votedFor.length > 0 || peek
  const top = Math.max(0, ...counts.map((c) => c.count))
  const pending = message.pending || message.id.startsWith('pending:')

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(describeError(err).message)
    } finally {
      setBusy(false)
    }
  }

  const toggle = (id: number) => {
    if (closed || pending) return
    setSelected((cur) => (poll.allow_multiselect ? (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]) : cur.includes(id) ? [] : [id]))
  }

  const vote = () => void run(async () => {
    await votePoll(channelId, message, selected)
    setSelected([])
    setPeek(false)
  })

  const status = finalized ? 'Опрос завершён' : left ? `осталось ${left}` : 'Подводим итоги…'
  return (
    <section className={styles.card} aria-label={`Опрос: ${poll.question.text ?? ''}`}>
      <h3 className={styles.question}>{poll.question.text}</h3>
      {!results ? <p className={styles.hint}>{poll.allow_multiselect ? 'Выберите один или несколько вариантов' : 'Выберите один вариант'}</p> : null}
      <div className={styles.answers} role={results ? 'list' : 'group'}>
        {poll.answers.map((a) => {
          const count = countOf(a.answer_id)
          const pct = total ? Math.round((count / total) * 100) : 0
          const emoji = a.poll_media.emoji
          const winner = finalized && count > 0 && count === top
          const own = votedFor.includes(a.answer_id)
          if (results) {
            return (
              <div key={a.answer_id} role="listitem" className={cx(styles.answer, styles.result, own && styles.own, winner && styles.winner)}>
                <span className={styles.bar} style={{ width: `${pct}%` }} aria-hidden="true" />
                {emoji ? <EmojiGlyph emoji={emoji} size={20} /> : null}
                <span className={styles.text}>{a.poll_media.text}</span>
                {own ? <Check size={16} className={styles.check} aria-label="Ваш голос" /> : null}
                <span className={styles.numbers}>
                  <span className={styles.count}>{votesLabel(count)}</span>
                  <strong className={styles.pct}>{pct} %</strong>
                </span>
              </div>
            )
          }
          const on = selected.includes(a.answer_id)
          return (
            <button
              key={a.answer_id}
              type="button"
              className={cx(styles.answer, on && styles.selected)}
              role={poll.allow_multiselect ? 'checkbox' : 'radio'}
              aria-checked={on}
              disabled={busy || pending}
              onClick={() => toggle(a.answer_id)}
            >
              {emoji ? <EmojiGlyph emoji={emoji} size={20} /> : null}
              <span className={styles.text}>{a.poll_media.text}</span>
              <span className={cx(styles.mark, poll.allow_multiselect ? styles.square : styles.round, on && styles.markOn)} aria-hidden="true">{on ? <Check size={12} strokeWidth={3} /> : null}</span>
            </button>
          )
        })}
      </div>
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      <footer className={styles.footer}>
        <span className={styles.meta}>
          {total > 0 ? (
            <button type="button" className={styles.link} onClick={() => setVoters([...counts].sort((x, y) => y.count - x.count)[0]?.id ?? poll.answers[0]?.answer_id ?? null)}>
              <Users size={14} aria-hidden="true" /> {votesLabel(total)}
            </button>
          ) : (
            <span>{votesLabel(0)}</span>
          )}
          <span aria-hidden="true">·</span>
          <span>{status}</span>
        </span>
        <span className={styles.actions}>
          {!closed && peek && votedFor.length === 0 ? <button type="button" className={styles.link} onClick={() => setPeek(false)}>К голосованию</button> : null}
          {!closed && !results ? <button type="button" className={styles.link} disabled={pending} onClick={() => setPeek(true)}>Показать итоги</button> : null}
          {!closed && votedFor.length > 0 ? <button type="button" className={styles.link} disabled={busy} onClick={() => void run(() => votePoll(channelId, message, []))}>Отменить голос</button> : null}
          {!closed && mine && !pending ? <button type="button" className={styles.link} disabled={busy} onClick={() => void run(() => endPoll(channelId, message.id))}>Завершить опрос</button> : null}
          {!results ? <button type="button" className={styles.primary} disabled={busy || pending || selected.length === 0} onClick={vote}>Проголосовать</button> : null}
        </span>
      </footer>
      {voters !== null ? <PollVoters message={message} channelId={channelId} initial={voters} onClose={() => setVoters(null)} /> : null}
    </section>
  )
}

/** Кто голосовал: варианты слева, люди выбранного варианта справа. */
function PollVoters({ message, channelId, initial, onClose }: { message: Message; channelId: string; initial: number; onClose: () => void }) {
  const poll = message.poll as Poll
  const [answer, setAnswer] = useState(initial)
  const [list, setList] = useState<{ answer: number; users: User[] } | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    socialApi.pollVoters(channelId, message.id, answer)
      .then((r) => { if (!cancelled) setList({ answer, users: r.users }) })
      .catch((err: unknown) => { if (!cancelled) setError(describeError(err).message) })
    return () => { cancelled = true }
  }, [channelId, message.id, answer])

  const counts = poll.results?.answer_counts ?? []
  const users = list?.answer === answer ? list.users : null
  return (
    <Modal title="Голоса" onClose={onClose} width={560}>
      <div className={styles.voters}>
        <nav className={styles.voterTabs} aria-label="Варианты">
          {poll.answers.map((a) => (
            <button key={a.answer_id} type="button" className={cx(styles.voterTab, a.answer_id === answer && styles.voterTabOn)} aria-current={a.answer_id === answer} onClick={() => setAnswer(a.answer_id)}>
              {a.poll_media.emoji ? <EmojiGlyph emoji={a.poll_media.emoji} size={18} /> : null}
              <span className={styles.text}>{a.poll_media.text}</span>
              <span className={styles.count}>{counts.find((c) => c.id === a.answer_id)?.count ?? 0}</span>
            </button>
          ))}
        </nav>
        <div className={styles.voterList}>
          {error ? <p className={styles.error}>{error}</p> : null}
          {!error && !users ? <p className={styles.hint}>Загружаем…</p> : null}
          {users && users.length === 0 ? <p className={styles.hint}>За этот вариант пока никто не голосовал</p> : null}
          {users?.map((u) => (
            <div key={u.id} className={styles.voter}>
              <Avatar initial={displayName(u).slice(0, 1).toUpperCase()} color="#5865F2" size={28} src={avatarUrl(u.id, u.avatar)} decoration={u.avatar_decoration_data} />
              <span className={styles.text}>{displayName(u)}</span>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  )
}
